import React, { useEffect, useCallback } from 'react';
import { FaChevronLeft, FaChevronRight, FaGripVertical } from 'react-icons/fa';
import { QBTestQuestionEto } from '../lib/api/types';
import { useDraggable } from '../hooks/useDraggable';
import { useTenantStore } from '../store/useTenantStore';
import { useUserStore } from '../store/useUserStore';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface DraggableQuestionNavProps {
  questions: QBTestQuestionEto[];
  currentIndex: number;
  totalQuestions?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
  onQuestionSelect: (index: number) => void;
  timedQuestionMode?: boolean;
  completedQuestions?: Set<string>;
}

const DraggableQuestionNav: React.FC<DraggableQuestionNavProps> = ({
  questions,
  currentIndex,
  onQuestionSelect,
  timedQuestionMode = false,
  completedQuestions = new Set()
}) => {
  const tenantId = useTenantStore(state => state.tenantId);
  const userInfo = useUserStore(state => state.userInfo);
  
  // Convert pixel position to percentage
  const convertToPercent = (position: { x: number; y: number }) => {
    return {
      xPercent: (position.x / window.innerWidth) * 100,
      yPercent: (position.y / window.innerHeight) * 100,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight
    };
  };

  // Convert percentage to pixels
  const convertToPixels = (percentPosition: any) => {
    return {
      x: (percentPosition.xPercent / 100) * window.innerWidth,
      y: (percentPosition.yPercent / 100) * window.innerHeight
    };
  };

  // Load position from Firebase
  const loadNavPosition = async () => {
    if (!tenantId || !userInfo?.id) return null;
    
    try {
      const docRef = doc(db, 'uiPreferences', `${tenantId}_${userInfo.id}`);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const prefs = docSnap.data();
        if (prefs?.navPosition) {
          return convertToPixels(prefs.navPosition);
        }
      }
    } catch (error) {
      console.error('Error loading nav position:', error);
    }
    
    // Also check localStorage as fallback
    const localKey = `navPosition_${tenantId}_${userInfo.id}`;
    const localData = localStorage.getItem(localKey);
    if (localData) {
      try {
        const position = JSON.parse(localData);
        if (position.xPercent !== undefined) {
          return convertToPixels(position);
        }
      } catch {}
    }
    
    
    return null;
  };

  // Save position to Firebase
  const saveNavPosition = useCallback(async (position: { x: number; y: number }) => {
    if (!tenantId || !userInfo?.id) return;
    
    const percentPosition = convertToPercent(position);
    
    // Save to localStorage first
    const localKey = `navPosition_${tenantId}_${userInfo.id}`;
    localStorage.setItem(localKey, JSON.stringify(percentPosition));
    
    // Save to Firebase
    try {
      const docRef = doc(db, 'uiPreferences', `${tenantId}_${userInfo.id}`);
      await setDoc(docRef, {
        tenantId: tenantId,
        userId: userInfo.id,
        navPosition: percentPosition,
        updatedAt: Date.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving nav position to Firebase:', error);
    }
  }, [tenantId, userInfo]);
  
  // Initialize with default position
  const getDefaultPosition = () => {
    // Position on the right side with 20% margin, centered vertically
    const rightMargin = window.innerWidth * 0.2; // 20% margin from right
    const navWidth = 120; // Approximate width of the minimal navigation
    const x = window.innerWidth - rightMargin - navWidth;
    
    // Ensure at least 20% space from bottom
    const bottomLimit = window.innerHeight * 0.8; // Maximum y position (80% of screen height)
    const centerY = window.innerHeight / 2 - 20; // Center vertically (20 is half the approximate height)
    const y = Math.min(centerY, bottomLimit - 40); // 40 is approximate nav height
    
    return { x, y };
  };

  const { ref, style, setPosition } = useDraggable({
    initialPosition: getDefaultPosition(),
    bounds: 'viewport',
    handle: '.drag-handle',
    onDragEnd: saveNavPosition
  });
  
  // Load saved position after component mounts
  useEffect(() => {
    const initPosition = async () => {
      const savedPosition = await loadNavPosition();
      if (savedPosition) {
        setPosition(savedPosition);
      }
    };
    
    if (tenantId && userInfo?.id) {
      initPosition();
    }
  }, [tenantId, userInfo?.id, setPosition]);

  // Handle window resize to maintain percentage-based positioning
  useEffect(() => {
    const handleResize = () => {
      // Load the position again to recalculate based on new window size
      loadNavPosition().then(savedPosition => {
        if (savedPosition) {
          setPosition(savedPosition);
        }
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setPosition]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          handlePrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, questions.length]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      // In TimedQuestion mode, prevent going back to completed questions
      if (timedQuestionMode) {
        const targetQuestion = questions[currentIndex - 1];
        if (targetQuestion && targetQuestion.questionId && completedQuestions.has(targetQuestion.questionId)) {
          return; // Don't allow navigation to completed questions
        }
      }
      onQuestionSelect(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      onQuestionSelect(currentIndex + 1);
    }
  };

  return (
    <div
      ref={ref}
      style={style}
      className="z-40 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200/30 dark:border-gray-700/30 p-2"
    >
      <div className="flex items-center gap-1">
        {/* Drag Handle */}
        <div className="drag-handle cursor-move p-1.5 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 rounded transition-colors">
          <FaGripVertical className="text-gray-400 w-3 h-3" />
        </div>

        {/* Previous Button */}
        <button
          onClick={handlePrevious}
          className={`p-1.5 rounded transition-colors ${
            currentIndex === 0 || (timedQuestionMode && currentIndex > 0 && questions[currentIndex - 1]?.questionId && completedQuestions.has(questions[currentIndex - 1].questionId!))
              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-700/50'
          }`}
          disabled={currentIndex === 0 || (timedQuestionMode && currentIndex > 0 && questions[currentIndex - 1]?.questionId && completedQuestions.has(questions[currentIndex - 1].questionId!)) || false}
          aria-label="Önceki soru"
        >
          <FaChevronLeft className="w-3.5 h-3.5" />
        </button>

        {/* Question Info */}
        <div className="text-center px-2">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            {currentIndex + 1}/{questions.length}
            {timedQuestionMode && currentIndex > 0 && questions[currentIndex - 1]?.questionId && completedQuestions.has(questions[currentIndex - 1].questionId!) && (
              <div className="text-xs text-orange-500 mt-0.5">🔒</div>
            )}
          </div>
        </div>

        {/* Next Button */}
        <button
          onClick={handleNext}
          className={`p-1.5 rounded transition-colors ${
            currentIndex === questions.length - 1 
              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-700/50'
          }`}
          disabled={currentIndex === questions.length - 1}
          aria-label="Sonraki soru"
        >
          <FaChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default DraggableQuestionNav;